import React from 'react'
import { ValidatedInput, ValidatedSelect, ValidatedTextarea, VALIDATORS } from '../../validators'
import { FormFieldMapping } from '../services/fieldMappingService'
import type { ValidationState } from '../../validators/types'

interface ValidatedDynamicFormFieldProps {
  field: FormFieldMapping
  value: any
  onChange: (fieldName: string, value: any) => void
  error?: string
  validationState?: ValidationState[string]
  onValidation?: (fieldName: string, isValid: boolean, message?: string) => void
}

// Map Salesforce field types to form input types
const mapSalesforceTypeToInputType = (salesforceType: string): string => {
  switch (salesforceType) {
    case 'string':
    case 'text': 
      return 'text'
    case 'textarea':
      return 'textarea'
    case 'email':
      return 'email'
    case 'phone':
      return 'phone'
    case 'url':
      return 'url'
    case 'select':
    case 'picklist':
      return 'select'
    case 'boolean':
      return 'boolean'
    case 'date':
    case 'datetime':
      return 'date'
    case 'number':
    case 'double':
    case 'int':
      return 'number'
    case 'currency':
      return 'currency'
    case 'percent':
      return 'percentage'
    default:
      return 'text'
  }
}

// US States for select fields
const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
]

// Business types for select fields
const BUSINESS_TYPES = [
  { value: '', label: 'Select Business Type' },
  { value: 'LLC', label: 'Limited Liability Company (LLC)' },
  { value: 'Corporation', label: 'Corporation' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
  { value: 'S-Corp', label: 'S-Corporation' },
  { value: 'C-Corp', label: 'C-Corporation' },
  { value: 'LLP', label: 'Limited Liability Partnership (LLP)' },
  { value: 'Non-Profit', label: 'Non-Profit Organization' },
  { value: 'Other', label: 'Other' }
]

const ValidatedDynamicFormField: React.FC<ValidatedDynamicFormFieldProps> = ({
  field,
  value,
  onChange,
  error,
  validationState,
  onValidation
}) => {
  // Use Salesforce field type if available, fallback to form field type
  const effectiveFieldType = field.salesforceFieldType 
    ? mapSalesforceTypeToInputType(field.salesforceFieldType)
    : field.formFieldType

  console.log(`🔄 Field ${field.formFieldName}: SF type=${field.salesforceFieldType}, effective=${effectiveFieldType}`)

  // Get validators from field mapping
  const validators = (field.validationRules?.validators || [])
    .map((validatorId: string) => VALIDATORS[validatorId])
    .filter(Boolean)

  // Add required validator if field is required
  if (field.isRequired && !validators.some(v => v === VALIDATORS.required)) {
    validators.unshift(VALIDATORS.required)
  }

  const getSelectOptions = () => {
    // 1. First priority: Salesforce picklist values (from enhanced field mappings)
    if (field.salesforcePicklistOptions && field.salesforcePicklistOptions.length > 0) {
      return field.salesforcePicklistOptions.map(option => ({
        value: option.value,
        label: option.label
      }))
    }
    
    // 2. Second priority: Special hardcoded field cases (for backwards compatibility)
    if (field.formFieldName.toLowerCase().includes('state')) {
      return US_STATES
    }
    if (field.formFieldName.toLowerCase().includes('businesstype') || 
        field.formFieldName.toLowerCase().includes('business_type')) {
      return BUSINESS_TYPES
    }
    
    // 3. Third priority: Validation rules contain options
    if (field.validationRules?.options) {
      return field.validationRules.options
    }
    
    // 4. Default empty select
    return [{ value: '', label: `Select ${field.formFieldLabel}` }]
  }

  const handleChange = (name: string, newValue: any) => {
    onChange(field.formFieldName, newValue)
  }

  // Determine input type for HTML input element
  const getInputType = (): 'text' | 'email' | 'tel' | 'password' | 'url' | 'number' | 'date' => {
    switch (effectiveFieldType) {
      case 'email': return 'email'
      case 'phone': return 'tel'
      case 'url': return 'url'
      case 'date': return 'date'
      case 'number': return 'number'
      case 'currency': return 'number'
      case 'percentage': return 'number'
      default: return 'text'
    }
  }

  // Convert validators array to FieldValidationConfig format
  const getValidationConfig = () => {
    const validatorConfigs = (field.validationRules?.validators || [])
      .filter(validatorId => validatorId !== 'required') // Don't duplicate required
      .map(validatorId => ({
        name: validatorId
      }))

    return {
      required: field.isRequired,
      validators: validatorConfigs,
      validateOnChange: true,
      validateOnBlur: true,
      formatOnInput: validators.some(v => v.formatInput),
      showSuccessIcon: true
    }
  }

  // Render the appropriate validated component
  const renderField = () => {
    const commonProps = {
      name: field.formFieldName,
      value: value || '',
      onChange: handleChange,
      placeholder: field.placeholder || field.formFieldLabel,
      validationConfig: getValidationConfig(),
      validationState: validationState,
      onValidation: onValidation,
      label: field.formFieldLabel,
      helpText: field.helpText,
      required: field.isRequired
    }

    switch (effectiveFieldType) {
      case 'textarea':
        return (
          <ValidatedTextarea
            {...commonProps}
            rows={3}
          />
        )

      case 'select':
        const options = getSelectOptions()
        return (
          <ValidatedSelect
            {...commonProps}
            options={options}
          />
        )

      case 'boolean':
        return (
          <div className="form-check mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              checked={value || false}
              onChange={(e) => handleChange(field.formFieldName, e.target.checked)}
              id={field.formFieldName}
            />
            <label className="form-check-label" htmlFor={field.formFieldName}>
              {field.formFieldLabel}
              {field.isRequired && <span className="text-danger ms-1">*</span>}
            </label>
            {field.helpText && (
              <div className="form-text text-muted">
                {field.helpText}
              </div>
            )}
            {error && (
              <div className="invalid-feedback" style={{ display: 'block' }}>
                {error}
              </div>
            )}
          </div>
        )

      default:
        const inputProps = {
          ...commonProps,
          type: getInputType()
        }

        // Add specific props for number-like fields
        const numberProps: { step?: string; min?: string; max?: string } = {}
        
        if (effectiveFieldType === 'currency') {
          numberProps.step = '0.01'
          numberProps.min = '0'
          inputProps.placeholder = field.placeholder || '0.00'
        } else if (effectiveFieldType === 'percentage') {
          numberProps.step = '0.01'
          numberProps.min = '0'
          numberProps.max = '100'
          inputProps.placeholder = field.placeholder || '0.00'
        } else if (effectiveFieldType === 'number') {
          numberProps.step = 'any'
        } else if (effectiveFieldType === 'phone') {
          inputProps.placeholder = field.placeholder || '(555) 123-4567'
        } else if (effectiveFieldType === 'url') {
          inputProps.placeholder = field.placeholder || 'https://example.com'
        }

        return <ValidatedInput {...inputProps} {...numberProps} />
    }
  }

  return renderField()
}

export default ValidatedDynamicFormField
